import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type {
  Transaction,
  TransactionCategorySuggestion,
  Category,
  Account,
  AccountDetails,
  Tenant,
  Profile,
  SharingConfiguration,
  SharingRequest,
  SharingResponse,
  CreateSharingRequestCommand,
  TransactionMetadata,
  TransactionAttachment,
  UpdateTransactionRequest,
  CreateTransactionRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateAccountRequest,
  UpdateAccountRequest,
  CreateTransactionsMetaDataCommand,
  ApiError,
} from '@/types/api';
import { getAccessToken } from '@/features/auth/tokenProvider';
import { emitToast } from '@/utils/toast';

/**
 * API Client configuration
 */
const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] || 'https://localhost:7001/api/v1';

/**
 * Create axios instance with default configuration
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    async config => {
      try {
        const token = await getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Silently handle token retrieval errors
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle common errors
  client.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        emitToast({
          message: 'Authentication failed. Please log in again.',
          severity: 'error',
        });
      } else if (error.response?.status >= 500) {
        emitToast({
          message: 'Server error. Please try again later.',
          severity: 'error',
        });
      } else if (error.code === 'ECONNABORTED') {
        emitToast({
          message: 'Request timeout. Please check your connection.',
          severity: 'error',
        });
      }
      return Promise.reject(error);
    }
  );

  return client;
};

const apiClient = createApiClient();

/**
 * Maps various error types to a unified ApiError shape
 */
export const mapToApiError = (error: unknown): ApiError => {
  // Handle axios errors
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { status?: number; data?: Record<string, unknown> } };

    // Extract error details from response
    const statusCode = axiosError.response?.status;
    const responseData = axiosError.response?.data;

    // Try to extract message from various response formats
    let message = 'An unexpected error occurred';
    let details: string | undefined;

    if (responseData) {
      if (typeof responseData === 'string') {
        message = responseData;
      } else if (responseData['message']) {
        message = String(responseData['message']);
        details = responseData['details']
          ? String(responseData['details'])
          : responseData['error']
            ? String(responseData['error'])
            : undefined;
      } else if (responseData['title']) {
        message = String(responseData['title']);
        details = responseData['detail'] ? String(responseData['detail']) : undefined;
      } else if (responseData['error']) {
        message = String(responseData['error']);
      }
    }

    // Provide user-friendly messages for common HTTP status codes
    if (statusCode) {
      switch (statusCode) {
        case 400:
          message = message || 'Invalid request. Please check your input.';
          break;
        case 401:
          message = 'Authentication required. Please log in again.';
          break;
        case 403:
          message = 'Access denied. You do not have permission to perform this action.';
          break;
        case 404:
          message = 'The requested resource was not found.';
          break;
        case 409:
          message = message || 'Conflict. The operation could not be completed.';
          break;
        case 422:
          message = message || 'Validation failed. Please check your input.';
          break;
        case 429:
          message = 'Too many requests. Please wait before trying again.';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          break;
        case 502:
        case 503:
        case 504:
          message = 'Service temporarily unavailable. Please try again later.';
          break;
      }
    }

    const result: ApiError = { message };
    if (details) {
      result.details = details;
    }
    if (statusCode) {
      result.statusCode = statusCode;
    }
    return result;
  }

  // Handle timeout errors
  if (error && typeof error === 'object' && 'code' in error && error.code === 'ECONNABORTED') {
    return {
      message: 'Request timeout. Please check your connection.',
      details: 'The request took too long to complete.',
    };
  }

  // Handle network errors
  if (error && typeof error === 'object' && 'code' in error && error.code === 'NETWORK_ERROR') {
    return {
      message: 'Network error. Please check your internet connection.',
      details: 'Unable to connect to the server.',
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    const result: ApiError = { message: error.message || 'An unexpected error occurred' };
    if (error.stack) {
      result.details = error.stack;
    }
    return result;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
    };
  }

  // Fallback for unknown error types
  const result: ApiError = { message: 'An unexpected error occurred' };
  if (typeof error === 'object') {
    result.details = JSON.stringify(error);
  } else {
    result.details = String(error);
  }
  return result;
};

/**
 * API service that implements the IHostApi interface
 * Uses real API calls to the backend
 */
export class ApiService {
  // Tenant access ID refresh
  async refreshAccessId(): Promise<Tenant> {
    const response: AxiosResponse<Tenant> = await apiClient.post('/tenant/refresh-access-id');
    return response.data;
  }

  // Activate shared tenant
  async activateSharedTenant(sharedTenantId: number, active: boolean): Promise<Tenant> {
    const response: AxiosResponse<Tenant> = await apiClient.put(
      `/tenant/shared/${sharedTenantId}/activate`,
      { active }
    );
    return response.data;
  }

  // Update profile
  async updateProfile(profile: Profile): Promise<Profile> {
    const response: AxiosResponse<Profile> = await apiClient.put('/profiles', profile);
    return response.data;
  }

  // Action response for sharing
  async actionSharingResponse(responseId: number, accept: boolean): Promise<SharingResponse> {
    const response: AxiosResponse<SharingResponse> = await apiClient.put(
      `/sharing/response/${responseId}/accept/${accept}`
    );
    return response.data;
  }
  private static instance: ApiService;

  private constructor() {}

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  // Health check
  async healthCheck(): Promise<void> {
    // Server health check is not versioned; call absolute origin
    const origin = new URL(API_BASE_URL).origin;
    await axios.get(`${origin}/api/healthcheck`);
  }

  // Transaction methods
  async getTransactions(): Promise<Transaction[]> {
    const response: AxiosResponse<Transaction[]> = await apiClient.get('/transactions');
    return response.data;
  }

  async fetchTransactions(hash: string): Promise<Transaction[]> {
    const response: AxiosResponse<Transaction[]> = await apiClient.get(
      `/transactions/fetch/${hash}`
    );
    return response.data;
  }

  async fetchTransactionsByAccount(accountId: string, hash?: string): Promise<Transaction[]> {
    const url = hash
      ? `transactions/account/${accountId}/fetch/${hash}`
      : `transactions/account/${accountId}/fetch`;
    const response: AxiosResponse<Transaction[]> = await apiClient.get(url);
    return response.data;
  }

  async updateTransaction(
    accountId: string,
    transactionId: number,
    transaction: UpdateTransactionRequest
  ): Promise<Transaction> {
    const response: AxiosResponse<Transaction> = await apiClient.put(
      `/transactions/account/${accountId}/${transactionId}`,
      transaction
    );
    return response.data;
  }

  async addTransaction(accountId: string, request: CreateTransactionRequest): Promise<Transaction> {
    const response: AxiosResponse<Transaction> = await apiClient.post(
      `/accounts/${accountId}/transactions`,
      request
    );
    return response.data;
  }

  async getCategorySuggestions(
    accountId: string,
    transactionIds: number[]
  ): Promise<TransactionCategorySuggestion[]> {
    const response = await apiClient.post(`/transactions/account/${accountId}/suggestions`, {
      accountId,
      transactionIds,
    });
    return response.data;
  }

  // Transaction metadata methods
  async addTransactionMetadata(
    accountId: string,
    transactionId: number,
    createTransactionsMetaDataCommand: CreateTransactionsMetaDataCommand
  ): Promise<TransactionMetadata> {
    const response: AxiosResponse<TransactionMetadata> = await apiClient.post(
      `/transactions/account/${accountId}/${transactionId}/metadata`,
      createTransactionsMetaDataCommand
    );
    return response.data;
  }

  async replaceTransactionMetadata(
    accountId: string,
    transactionId: number,
    metadataList: Array<{ amount: number; description: string; category: string }>
  ): Promise<TransactionMetadata[]> {
    const response: AxiosResponse<TransactionMetadata[]> = await apiClient.put(
      `/account/${accountId}/${transactionId}/metadata`,
      { TransactionMetadata: metadataList }
    );
    return response.data;
  }

  // Transaction attachment methods
  async addTransactionAttachment(
    accountId: string,
    transactionId: number,
    attachment: TransactionAttachment,
    content: File
  ): Promise<TransactionAttachment> {
    const formData = new FormData();
    // API expects a single file field named 'file'
    formData.append('file', content, attachment.name ?? 'attachment');

    const response: AxiosResponse<TransactionAttachment> = await apiClient.post(
      `/accounts/${accountId}/transactions/${transactionId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async deleteTransactionAttachment(
    accountId: string,
    transactionId: number,
    attachmentId: number
  ): Promise<void> {
    await apiClient.delete(
      `/accounts/${accountId}/transactions/${transactionId}/attachments/${attachmentId}`
    );
  }

  async getTransactionAttachment(
    accountId: string,
    transactionId: number,
    attachmentId: number
  ): Promise<Blob> {
    const response: AxiosResponse<Blob> = await apiClient.get(
      `/accounts/${accountId}/transactions/${transactionId}/attachments/${attachmentId}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  // Category methods
  async getCategories(): Promise<Category[]> {
    const response: AxiosResponse<Category[]> = await apiClient.get('/categories');
    return response.data;
  }

  async updateCategory(categoryId: number, category: UpdateCategoryRequest): Promise<Category> {
    const response: AxiosResponse<Category> = await apiClient.put(
      `/categories/${categoryId}`,
      category
    );
    return response.data;
  }

  async addCategory(category: CreateCategoryRequest): Promise<Category> {
    const response: AxiosResponse<Category> = await apiClient.post('/categories', category);
    return response.data;
  }

  async deleteCategory(categoryId: number): Promise<void> {
    await apiClient.delete(`/categories/${categoryId}`);
  }

  // Account methods
  async getAccounts(): Promise<Account[]> {
    const response: AxiosResponse<Account[]> = await apiClient.get('/accounts');
    return response.data;
  }

  async updateAccount(accountId: string, account: UpdateAccountRequest): Promise<Account> {
    const response: AxiosResponse<Account> = await apiClient.put(`/accounts/${accountId}`, account);
    return response.data;
  }

  async addAccount(account: CreateAccountRequest): Promise<Account> {
    const response: AxiosResponse<Account> = await apiClient.post('/accounts', account);
    return response.data;
  }

  async fetchAccountDetails(account: Account): Promise<AccountDetails[]> {
    const response: AxiosResponse<AccountDetails[]> = await apiClient.post(
      '/accounts/fetch',
      account
    );
    return response.data;
  }

  async deleteAccount(accountId: string): Promise<void> {
    await apiClient.delete(`/accounts/${accountId}`);
  }

  // Tenant methods
  async getTenant(): Promise<Tenant> {
    const response: AxiosResponse<Tenant> = await apiClient.get('/tenants/current');
    return response.data;
  }

  async saveTenant(tenant: Tenant): Promise<Tenant> {
    const response: AxiosResponse<Tenant> = await apiClient.put('/tenant', tenant);
    return response.data;
  }

  async createTenant(tenant: Tenant): Promise<Tenant> {
    const response: AxiosResponse<Tenant> = await apiClient.post('/tenant', tenant);
    return response.data;
  }

  // Profile methods
  async getProfile(): Promise<Profile> {
    const response: AxiosResponse<Profile> = await apiClient.get('/profiles');
    return response.data;
  }

  // Sharing methods
  async createSharingRequest(command: CreateSharingRequestCommand): Promise<SharingRequest> {
    const response: AxiosResponse<SharingRequest> = await apiClient.post(
      '/sharing/request',
      command
    );
    return response.data;
  }

  async getSharingConfiguration(): Promise<SharingConfiguration> {
    const response: AxiosResponse<SharingConfiguration> = await apiClient.get('/sharing');
    return response.data;
  }

  async respondToSharingRequest(responseId: number, accept: boolean): Promise<SharingResponse> {
    const response: AxiosResponse<SharingResponse> = await apiClient.put(
      `/sharing/response/${responseId}/accept/${accept}`
    );
    return response.data;
  }

  async deleteSharingRequest(requestId: number): Promise<void> {
    await apiClient.delete(`/sharing/request/${requestId}`);
  }
}

// Export singleton instance
export const apiService = ApiService.getInstance();
export default apiService;
